import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from 'core/layout/MainLayout';
import {
    Box,
    Button,
    Container,
    Typography,
    TextField,
    Paper,
    Stack,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Avatar,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import useProfile from 'features/feature_user/hooks/UseProfile';
import useSaveProfile from 'features/feature_user/hooks/UserSaveProfile';
import SelectCountry from 'core/components/shared/select/SelectCountry';
import LoadingInside from 'core/components/shared/LoadingInside';
import { CVHttpService } from '../services';

function AICVBuilderPage() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [userInfo, setUserInfo] = useState('');
    const [jobInfo, setJobInfo] = useState('');
    const [showJobInfo, setShowJobInfo] = useState(false);
    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const [profileDataText, setProfileDataText] = useState('');
    const [loadingProfileData, setLoadingProfileData] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [generatedCV, setGeneratedCV] = useState(null);
    const { data: profile, isLoading: profileLoading, refetch: refetchProfile } = useProfile();
    const { mutate: saveProfile, isLoading: savingProfile } = useSaveProfile();

    // Check if profile is complete
    const isProfileComplete = useMemo(() => {

        if (!profile) return false;
        return !!(
            profile.first_name &&
            profile.last_name &&
            profile.email &&
            profile.phone_number &&
            profile.city &&
            profile.home_address &&
            profile.about
        );
    }, [profile]);

    // Profile edit form
    const { register, handleSubmit, reset, control } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            homeAddress: '',
            postalCode: '',
            city: '',
            country: '',
            phoneNumber: '',
            about: '',
        },
    });

    // Load profile data for AI when component mounts
    useEffect(() => {
        const loadProfileDataForAI = async () => {
            if (!profile) return;
            
            setLoadingProfileData(true);
            try {
                const profileData = await CVHttpService.getUserProfileDataForAI();
                const formattedText = CVHttpService.formatProfileDataForAI(profile, profileData);
                setProfileDataText(formattedText);
            } catch (error) {
                console.error('Error loading profile data for AI:', error);
                enqueueSnackbar('Failed to load profile data', { variant: 'error' });
            } finally {
                setLoadingProfileData(false);
            }
        };

        loadProfileDataForAI();
    }, [profile, enqueueSnackbar]);

    // Update form when profile loads or modal opens
    useEffect(() => {
        if (profile && profileModalOpen) {
            reset({
                firstName: profile.first_name || '',
                lastName: profile.last_name || '',
                homeAddress: profile.home_address || '',
                postalCode: profile.postalcode || '',
                city: profile.city || '',
                country: profile.country_id ? profile.country_id.toString() : '',
                phoneNumber: profile.phone_number || '',
                about: profile.about || '',
            });
        }
    }, [profile, profileModalOpen, reset]);

    const handleProfileSubmit = (data) => {
        const body = {
            id: profile.id,
            first_name: data.firstName,
            last_name: data.lastName,
            home_address: data.homeAddress,
            postalcode: data.postalCode,
            city: data.city,
            phone_number: data.phoneNumber,
            about: data.about,
        };

        if (data.country) {
            body.country_id = data.country;
        }

        saveProfile(body, {
            onSuccess: async () => {
                enqueueSnackbar('Profile updated successfully', { variant: 'success' });
                const updatedProfile = await refetchProfile();
                setProfileModalOpen(false);
                
                // Reload profile data for AI after profile update
                setLoadingProfileData(true);
                try {
                    const profileData = await CVHttpService.getUserProfileDataForAI();
                    const formattedText = CVHttpService.formatProfileDataForAI(updatedProfile.data || profile, profileData);
                    setProfileDataText(formattedText);
                } catch (error) {
                    console.error('Error reloading profile data:', error);
                } finally {
                    setLoadingProfileData(false);
                }
            },
            onError: () => {
                enqueueSnackbar('Failed to update profile', { variant: 'error' });
            },
        });
    };

    const getProfileImage = (image) => {
        if (!image) return '/default-avatar.png';
        if (image.includes('/api/uploaded_media/https')) {
            return image.replace('/api/uploaded_media/', '');
        }
        return image;
    };

    const handleImportFromProfile = () => {
        if (!profileDataText) {
            enqueueSnackbar('Profile data is still loading. Please wait...', { variant: 'info' });
            return;
        }

        setUserInfo((prev) => {
            const existing = prev.trim();
            return existing ? `${existing}\n\n${profileDataText}` : profileDataText;
        });
        
        enqueueSnackbar('Profile data imported successfully', { variant: 'success' });
    };

    const handleGenerate = async () => {
        if (!userInfo.trim()) {
            enqueueSnackbar('Please provide information about yourself or import from profile', { variant: 'warning' });
            return;
        }

        setGenerating(true);
        try {
            const response = await CVHttpService.generateCVWithAI({
                user_info: userInfo.trim(),
                job_info: jobInfo.trim() || null
            });

            console.log('CV Generation Response:', response);
            
            if (response && response.cv_id) {
                enqueueSnackbar('CV generated successfully!', { variant: 'success' });
                // Redirect to CV details page
                navigate(`/cv/${response.cv_id}`);
            } else if (response && response.cv_markdown) {
                // Fallback: if cv_id not available, show the markdown (shouldn't happen)
                setGeneratedCV(response.cv_markdown);
                enqueueSnackbar('CV generated successfully!', { variant: 'success' });
            } else {
                throw new Error('Invalid response format from server');
            }
        } catch (error) {
            console.error('Error generating CV:', error);
            const errorMessage = error.message || 'Failed to generate CV. Please try again.';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setGenerating(false);
        }
    };

    return (
        <MainLayout>
            <Container maxWidth="md" sx={{ mt: 6, mb: 8 }}>
                <Stack spacing={4}>
                    {/* Header */}
                    <Box>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/cv')}
                            sx={{ mb: 2 }}
                        >
                            Back to CVs
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                            <Box>
                                <Typography variant="h4" fontWeight={700}>
                                    Create CV with AI
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Provide information about yourself and let AI create your professional CV
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Profile Completion Alert */}
                    {!profileLoading && profile && !isProfileComplete && (
                        <Alert
                            severity="warning"
                            icon={<WarningIcon />}
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => setProfileModalOpen(true)}
                                >
                                    Complete Profile
                                </Button>
                            }
                            sx={{
                                borderRadius: 2,
                            }}
                        >
                            Your profile is incomplete. Please complete your profile to get better AI-generated CV results.
                        </Alert>
                    )}

                    {/* Main Content */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Stack spacing={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" fontWeight={600}>
                                    Tell us about yourself
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<PersonIcon />}
                                    onClick={handleImportFromProfile}
                                    disabled={profileLoading || !profile || loadingProfileData || !profileDataText}
                                    size="medium"
                                >
                                    {loadingProfileData ? 'Loading...' : 'Import from Profile'}
                                </Button>
                            </Box>

                            {/* Profile Incomplete Alert above textarea */}
                            {!profileLoading && profile && !isProfileComplete && (
                                <Alert
                                    severity="info"
                                    icon={<WarningIcon />}
                                    action={
                                        <Button
                                            color="inherit"
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() => setProfileModalOpen(true)}
                                        >
                                            Edit Profile
                                        </Button>
                                    }
                                    sx={{
                                        borderRadius: 2,
                                    }}
                                >
                                    You need to complete your profile to get the best results. Click "Edit Profile" to update your information.
                                </Alert>
                            )}

                            <TextField
                                multiline
                                rows={12}
                                fullWidth
                                placeholder="Enter additional information about yourself, such as:
• Professional summary or about yourself
• Work experience with job titles, companies, and dates
• Education background, degrees, and institutions
• Skills and certifications
• Languages you speak
• Achievements, awards, or notable projects
• Any other relevant professional information

You can also click 'Import from Profile' to automatically fill in your profile information."
                                value={userInfo}
                                onChange={(e) => setUserInfo(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/cv')}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                                    onClick={handleGenerate}
                                    disabled={!userInfo.trim() || generating}
                                    sx={{ px: 4 }}
                                >
                                    {generating ? 'Generating...' : 'Generate CV with AI'}
                                </Button>
                            </Box>
                        </Stack>
                    </Paper>

                    {/* Job Information Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                },
                            }}
                            onClick={() => setShowJobInfo(!showJobInfo)}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WorkOutlineIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    Job Information (Optional)
                                </Typography>
                            </Box>
                            {showJobInfo ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </Box>
                        {showJobInfo && (
                            <Box sx={{ p: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <TextField
                                    multiline
                                    rows={4}
                                    fullWidth
                                    placeholder="Enter information about the job you're applying for, such as job title, company name, job description, requirements, or any specific details that would help tailor your CV..."
                                    value={jobInfo}
                                    onChange={(e) => setJobInfo(e.target.value)}
                                    size="small"
                                    variant="standard"
                                    sx={{
                                        '& .MuiInput-underline:before': {
                                            borderBottom: 'none',
                                        },
                                        '& .MuiInput-underline:hover:before': {
                                            borderBottom: 'none',
                                        },
                                        '& .MuiInput-underline:after': {
                                            borderBottom: 'none',
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Paper>

                    {/* Info Box */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 2,
                            bgcolor: 'action.hover',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Stack spacing={1}>
                            <Typography variant="subtitle2" fontWeight={600}>
                                💡 Tips for best results:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                • Describe your work experience with job titles, companies, and dates
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                • Mention your education, degrees, and institutions
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                • List your key skills, certifications, and languages
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                • Add any achievements, awards, or notable projects
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                Note: Your contact information (name, email, phone) will be automatically included from your profile.
                            </Typography>
                        </Stack>
                    </Paper>
                </Stack>
            </Container>

            {/* Profile Edit Modal */}
            <Dialog
                open={profileModalOpen}
                onClose={() => setProfileModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                    },
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PersonIcon color="primary" />
                        <Typography variant="h6" fontWeight={600}>
                            Complete Your Profile
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <LoadingInside loading={profileLoading} />
                    <form onSubmit={handleSubmit(handleProfileSubmit)}>
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            {/* Avatar */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <Avatar
                                    alt={`${profile?.first_name || ''} ${profile?.last_name || ''}`}
                                    src={getProfileImage(profile?.image)}
                                    sx={{ width: 100, height: 100 }}
                                />
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="First Name *"
                                        {...register('firstName', { required: 'First name is required' })}
                                        placeholder="Enter your first name"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Last Name *"
                                        {...register('lastName', { required: 'Last name is required' })}
                                        placeholder="Enter your last name"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Controller
                                        name="country"
                                        control={control}
                                        render={({ field }) => (
                                            <SelectCountry
                                                {...field}
                                                title="Select Country"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="City *"
                                        {...register('city', { required: 'City is required' })}
                                        placeholder="Enter your city"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Street Address *"
                                        {...register('homeAddress', { required: 'Address is required' })}
                                        placeholder="Enter your street address"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Postal Code"
                                        {...register('postalCode')}
                                        placeholder="Enter postal code"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone Number *"
                                        {...register('phoneNumber', { required: 'Phone number is required' })}
                                        placeholder="Enter your phone number"
                                        type="tel"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="About Me *"
                                        {...register('about', { required: 'About section is required' })}
                                        placeholder="Tell us about yourself"
                                        multiline
                                        rows={4}
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    </form>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2 }}>
                    <Button onClick={() => setProfileModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit(handleProfileSubmit)}
                        disabled={savingProfile}
                        startIcon={savingProfile ? <CircularProgress size={20} /> : null}
                    >
                        {savingProfile ? 'Saving...' : 'Save Profile'}
                    </Button>
                </DialogActions>
            </Dialog>
        </MainLayout>
    );
}

export default AICVBuilderPage;

